import { TimelineTrack } from '../../types';
import { SNAPPING_CONFIG } from '../../constants';

/**
 * Snaps a value to the nearest grid point
 */
export const snapToGrid = (value: number): number => {
  return Math.round(value / SNAPPING_CONFIG.gridSize) * SNAPPING_CONFIG.gridSize;
};

/**
 * Enhanced snapping function that checks for both grid and edge snapping
 */
export const snapToGridAndEdges = (
  value: number,
  trackIndex: number,
  tracks: TimelineTrack[],
  excludeIds: string[] = []
): number => {
  // First, get basic grid snap
  const gridSnapped = snapToGrid(value);

  // Skip edge snapping for magnetic tracks or if no tracks available
  if (trackIndex < 0 || trackIndex >= tracks.length || tracks[trackIndex].magnetic) {
    return gridSnapped;
  }

  // Find all item edges in the target track and adjacent tracks for edge snapping
  const edgePositions: number[] = [];

  // Check current track and adjacent tracks for edge positions
  const tracksToCheck = [trackIndex];
  if (trackIndex > 0) tracksToCheck.push(trackIndex - 1);
  if (trackIndex < tracks.length - 1) tracksToCheck.push(trackIndex + 1);

  tracksToCheck.forEach(tIndex => {
    if (tIndex >= 0 && tIndex < tracks.length) {
      tracks[tIndex].items.forEach(item => {
        if (!excludeIds.includes(item.id)) {
          edgePositions.push(item.start); // Start edge
          edgePositions.push(item.end);   // End edge
        }
      });
    }
  });

  // Find the closest edge within snap tolerance
  let closestEdge: number | null = null;
  let minDistance = SNAPPING_CONFIG.edgeSnapTolerance;

  edgePositions.forEach(edge => {
    const distance = Math.abs(value - edge);
    if (distance < minDistance) {
      minDistance = distance;
      closestEdge = edge;
    }
  });

  // If we found a close edge and prioritize edge snapping, use it
  if (SNAPPING_CONFIG.prioritizeEdgeSnap && closestEdge !== null) {
    return closestEdge;
  }

  // Otherwise, check if grid snap or edge snap is closer to the original value
  if (closestEdge !== null) {
    const gridDistance = Math.abs(value - gridSnapped);
    const edgeDistance = Math.abs(value - closestEdge);

    return edgeDistance < gridDistance ? closestEdge : gridSnapped;
  }

  return gridSnapped;
};
